import {
  MyPatternListProps,
  RegionProps,
} from '../../../interface/aiAnalyze/aiRecommend/type';
import { SidoList } from '../../search/FilterData';
import {
  investmentTypeMap,
  periodMap,
  prcReliabilityMap,
  profitRateMap,
  profitStatusMap,
  purposeMap,
  revenueTypeMap,
  valueStatusMap,
} from './data';
import { PatternInfoProps } from './result/report/data';

interface ChipItem {
  key: string;
  label?: string;
  value: string;
}

const getRegionLabelFromPnu = (
  pnu: string,
  sigunguList: RegionProps[],
  emdList: RegionProps[],
) => {
  const sido = SidoList.find((s) => s.code === pnu.substring(0, 2))?.region;
  const sigungu =
    sigunguList.find((s) => s.code === pnu.substring(0, 5))?.region ?? '';
  const emd = emdList.find((e) => e.code === pnu.substring(0, 8))?.region ?? '';
  return { sido, sigungu, emd };
};

const getRangeLabel = ([start, end]: [number, number]) => {
  if (start === 1980 && end === 2025) {
    return '20년 이상';
  }

  if (start >= 2010 && end <= 2020) {
    return '5년 ~ 10년';
  }

  if (end <= 2015) {
    return '10년 이상';
  }

  if (start >= 2020) {
    return '5년 이하';
  }

  return '20년 이상';
};

const calculatePrice = (price: string | number) => {
  if (!price) return '';
  const value = Number(price);
  if (isNaN(value)) return '-';

  const eok = Math.floor(value / 100000000); // 억 단위
  const man = Math.round((value % 100000000) / 10000); // 만 단위 (반올림)

  let result = '';

  if (eok > 0) {
    result += `${eok.toLocaleString()}억`;
  }

  if (man > 0) {
    result += ` ${man.toLocaleString()}만`;
  }

  result += '원';
  return result.trim();
};

export const gradeInfo: Partial<
  Record<number, { color: string; bg: string; title: string }>
> = {
  1: { color: '#F45551', bg: '#FFEFEF', title: '요주의' },
  2: { color: '#09AB49', bg: '#EEFFF6', title: '보통' },
  3: { color: '#008AFF', bg: '#EAF2FE', title: '우수' },
  4: { color: '#008AFF', bg: '#EAF2FE', title: '매우우수' },
  5: { color: '#008AFF', bg: '#EAF2FE', title: '매우우수' },
  0: { color: '#616161', bg: '#616161', title: '데이터 없음' },
};

export const getInvestChips = (
  item: PatternInfoProps | MyPatternListProps,
  sigunguList: RegionProps[],
  emdList: RegionProps[],
): ChipItem[] => {
  const list: ChipItem[] = [];

  const { sido, sigungu, emd } = getRegionLabelFromPnu(
    item.pnu,
    sigunguList,
    emdList,
  );
  if (sido) {
    list.push({
      key: 'region',
      value: [sido, sigungu, emd].filter((r) => r !== '').join(' > '),
    });
  }

  // 물건 종류
  if (item.category) {
    list.push({
      key: 'category',
      value: item.category,
    });
  }

  // 물건종류 상세
  if (item.category_detail) {
    list.push({
      key: 'categoryDetail',
      value: item.category_detail,
    });
  }

  // 투자 기간
  if (item.investment_period !== 'A') {
    list.push({
      key: 'investmentPeriod',
      label: '투자기간',
      value: periodMap[item.investment_period],
    });
  }

  // 투자 목적
  if (item.investment_purpose !== 'A') {
    list.push({
      key: 'investmentPurpose',
      value: purposeMap[item.investment_purpose],
    });
  }

  // 노후년도
  if (item.aging_start_year && item.aging_end_year) {
    list.push({
      key: 'aging',
      label: '노후년도',
      value: getRangeLabel([
        Number(item.aging_start_year),
        Number(item.aging_end_year),
      ]),
    });
  }
  // 용도지역
  if (item.zoning) {
    item.zoning.split(',').forEach((ua: string, idx: number) => {
      list.push({
        key: `useArea.${idx}`,
        value: ua,
      });
    });
  }
  return list;
};

export const getProfitChips = (
  item: PatternInfoProps | MyPatternListProps,
): ChipItem[] => {
  const list: ChipItem[] = [];

  // 수익유형
  if (item.revenue_type) {
    list.push({
      key: 'revenueType',
      value: revenueTypeMap[item.revenue_type],
    });
  }

  // 부대비용 포함
  if (item.cost_included !== 'N') {
    list.push({
      key: 'costIncluded',
      value: '부대비용 포함',
    });
  }

  // 전체 투자금
  if (item.min_investment_value || item.max_investment_value) {
    list.push({
      key: 'investmentValue',
      label: '전체 투자금',
      value: `${calculatePrice(item.min_investment_value)}~${calculatePrice(item.max_investment_value)}`,
    });
  }

  // 투자금액 비율
  if (item.investment_rate) {
    list.push({
      key: 'investmentRate',
      label: investmentTypeMap[item.investment_type],
      value: `${item.investment_rate}%`,
    });
  }

  // 할인비율
  if (item.profit_rate !== 'A') {
    list.push({
      key: 'profitRate',
      label: '할인 비율',
      value: profitRateMap[item.profit_rate],
    });
  }

  // 유찰횟수
  if (item.min_bid_cnt || item.max_bid_cnt) {
    list.push({
      key: 'bidCnt',
      label: '유찰횟수',
      value: `${item?.min_bid_cnt ?? ''}~${item?.max_bid_cnt ?? ''}`,
    });
  }

  // 리스크 요인
  if (item.risk) {
    list.push({
      key: 'risk',
      value: '위험감수',
    });
    item.risk.split(',').forEach((ua: string, idx: number) => {
      list.push({
        key: `risk.${idx}`,
        value: ua,
      });
    });
  } else {
    list.push({
      key: 'risk',
      value: '위험회피',
    });
  }

  // 매각 수익
  if (item.profit_status) {
    list.push({
      key: 'profitStatus',
      value: profitStatusMap[item.profit_status],
    });
  }

  // 시장가치
  if (item.value_status) {
    list.push({
      key: 'valueStatus',
      value: valueStatusMap[item.value_status],
    });
  }

  // 가격 신뢰도
  if (item.prc_reliability) {
    list.push({
      key: 'prcReliability',
      label: '가격신뢰도',
      value: prcReliabilityMap[item.prc_reliability],
    });
  }

  // 사례 분석
  if (item.auction_date_years) {
    list.push({
      key: 'auctionDateYears',
      label: '사례분석',
      value:
        item.auction_date_years === '0'
          ? '전체 사례'
          : `최근 ${item.auction_date_years}년 이내`,
    });
  }

  return list;
};
